import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import { Chart, registerables, ScriptableContext } from 'chart.js';
import moment from 'moment';
import { convertBytesUnits } from '../../../../../../utils/convertBytesUnit';
import { HistogramDataResponse } from '../../../../../../dtos/reports-data-response';
import { NoResult } from '../../../../../shared/no-result/no-result';
import Loader from '../../../../../shared/loader/loader';
import './traffic-timeline-chart.scss';
import { getTimeUnit } from '../../../../../../utils/datesUtils';
import { TooltipLinePlugin } from '../../../../../../utils/chart';

Chart.register(...registerables);

interface TrafficTimelineChartProps {
    data: HistogramDataResponse;
    isLoading: boolean;
    isInbound: boolean;
}

const TrafficTimelineChart: React.FC<TrafficTimelineChartProps> = ({data, isLoading, isInbound}) => {
    const [chartData, setChartData] = useState<any>(null);
    const [options, setOptions] = useState<any>({});

    const color = useMemo(() => isInbound ? '0, 159, 219' : '0, 87, 184', [isInbound]);

    const getFormattedData = () => {
        return {
            labels: data.map(item => moment(item.time).format('YYYY-MM-DD HH:mm')),
            datasets: [
                {
                    label: 'Traffic',
                    data: data.map(item => ({x: item.time, y: item.bytes})),
                    fill: 'start',
                    backgroundColor: (context: ScriptableContext<'line'>) => {
                        const ctx = context.chart.ctx;
                        const gradientHeight = context.chart.height;
                        const gradient = ctx.createLinearGradient(0, 0, 0, gradientHeight);
                        gradient.addColorStop(0, `rgba(${color}, 0.20)`);
                        gradient.addColorStop(1, `rgba(${color}, 0.00)`);
                        return gradient;
                    },
                    tension: 0.2,
                    pointRadius: 0.01,
                    pointHoverRadius: 3,
                    borderWidth: 2,
                    borderColor: `rgba(${color}, 0.5)`,
                    hoverBorderWidth: 2,
                    pointBackgroundColor: 'white',
                }
            ]
        };
    };

    const calculateBytesUnit = (range: number) => {
        const unitPrefixes = ['K', 'M', 'G', 'T'];
        const base = 1000;
        let magnitude = Math.min(Math.floor(Math.log(range) / Math.log(base)), unitPrefixes.length);
        const divisor = range ? Math.pow(base, magnitude) : 1;
        let bytesUnit = unitPrefixes[--magnitude] || '';
        bytesUnit += 'B';
        return { bytesUnit, divisor };
    };

    useEffect(() => {
        if (data.length) {
            const formattedData = getFormattedData();
            const unit = getTimeUnit(data);
            const range = Math.max(...data.map(item => item.bytes)) - Math.min(...data.map(item => item.bytes));
            const stepSize = range ? Math.round(range / 5) : 1;
            const { bytesUnit, divisor } = calculateBytesUnit(range);

            setChartData(formattedData);
            setOptions({
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: unit,
                            displayFormats: {
                                hour: 'HH:mm',
                                day: 'M/D',
                                month: 'MMM YYYY'
                            }
                        },
                        display: true,
                        title: {
                            display: false,
                        },
                        grid: {
                            drawOnChartArea: false,
                        },
                    },
                    y: {
                        beginAtZero: true,
                        stepSize: stepSize,
                        ticks: {
                            callback: (value: any) => {
                                const val = value / divisor;
                                return Number.isInteger(val) ? `${val.toFixed(0)}` : `${val.toFixed(1)}`;
                            }
                        },
                        display: true,
                        title: {
                            display: false,
                        }
                    }
                },
                plugins: {
                    title: {
                        display: true,
                        text: `TRAFFIC (${bytesUnit})`,
                        align: 'start',
                        padding: 16,
                        color: '#13171B'
                    },
                    legend: {
                        display: false
                    },
                    tooltip: {
                        mode: 'index',
                        enabled: true,
                        position: 'nearest',
                        backgroundColor: 'rgba(0, 0, 0, 1)',
                        caretPadding: 8,
                        titleFont: {
                            size: 10,
                            font: 'ATTAleckSansBold'
                        },
                        bodyFont: {
                            size: 11,
                            font: 'ATTAleckSansBold'
                        },
                        boxWidth: 8,
                        boxHeight: 8,
                        padding: 12,
                        titleMarginBottom: 10,
                        bodySpacing: 8,
                        boxPadding: 5,
                        callbacks: {
                            title: (tooltipItems: any): string => {
                                const timeValue = tooltipItems[0]?.label;
                                if (timeValue) {
                                    return moment(timeValue as string).format('MMMM D, YYYY HH:mm');
                                }
                                return '';
                            },
                            label: (context: any): string => {
                                const yValue = Number(context.parsed.y);
                                const bytesUnit = convertBytesUnits(yValue);
                                return `${isInbound ? 'Inbound' : 'Outbound'}  ${bytesUnit.value}${bytesUnit.unitName}`;
                            },
                            labelColor: (context:any) => {
                                return {
                                    borderColor: '#fff',
                                    backgroundColor: `rgba(${color}, 1)`,
                                    borderWidth: 0.5,
                                    borderRadius: 4,
                                };
                            },
                        },
                    }
                }
            });
        }
    }, [data, isInbound]);

    const renderChartContent = () => {
        if (isLoading) {
            return <Loader type="component" />;
        }
        if (chartData) {
            return (
                <Line data={chartData} plugins={[TooltipLinePlugin]} options={options} />
            );
        }
        return <NoResult title="No data to display" subTitle="Change timeframe and try again" />;
    };

    return <div className="line-chart-container">{renderChartContent()}</div>;
};

export default TrafficTimelineChart;